import posthog from 'posthog-js';
import { create } from 'zustand';
import { shutdownIntercom } from '@/lib/support/intercom';
import { revokeToken } from '@/lib/auth/auth0';
import { clearAccessTokenOnService, clearRefreshToken, getRefreshToken } from '@/lib/auth/token-storage';
import { cancelTokenRefresh } from '@/lib/auth/token-refresh-scheduler';
import { clearUserData } from '@/lib/sync/clear-user-data';
import { useWorkspaceStore } from '@/stores/use-workspace-store';

export type AuthUserInfo = {
  sub: string;
  email: string;
  name: string;
  picture: string;
};

interface AuthState {
  user: AuthUserInfo | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginError: string | null;
  /**
   * Populated from GET /auth/me once authenticated. `undefined` means the
   * response hasn't arrived yet; `null` means the user hasn't completed
   * onboarding; a non-null string is the ISO timestamp of completion.
   */
  onboardingCompletedAt: string | null | undefined;

  /**
   * The Nula user UUID from /auth/me. Distinct from AuthUserInfo.sub, which
   * is the Auth0 subject — Intercom and the server-side contact both key on
   * this UUID.
   */
  nulaUserId: string | null;
  intercom: { appId: string; userJwt: string } | null;

  setFromAuth: (params: {
    user: AuthUserInfo | null;
    accessToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
  }) => void;

  /**
   * Swap only the access token, preserving the signed-in user and auth flags.
   * Used by the proactive token-refresh scheduler, which mints a fresh token in
   * the background without re-running the full sign-in flow.
   */
  setAccessToken: (accessToken: string) => void;

  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true,
  loginError: null,
  onboardingCompletedAt: undefined,
  nulaUserId: null,
  intercom: null,

  setFromAuth: ({ user, accessToken, isAuthenticated, isLoading }) => {
    set({ user, accessToken, isAuthenticated, isLoading });
  },

  setAccessToken: (accessToken) => {
    set({ accessToken });
  },

  logout: async () => {
    // Stop the proactive refresh timer so it can't mint a token for the
    // user we're signing out.
    cancelTokenRefresh();

    // Revoke the refresh token server-side first so a leaked copy can't be
    // used to mint new access tokens. Best-effort — if Auth0 is offline we
    // still complete local cleanup.
    const refreshToken = await getRefreshToken();
    if (refreshToken) {
      await revokeToken(refreshToken);
    }

    await clearAccessTokenOnService();
    // Drop the persisted refresh token so the next app launch doesn't
    // silently re-authenticate the previous user via useSessionResume.
    await clearRefreshToken();
    // NOTE: we intentionally KEEP the saved last-email so the Login screen can
    // offer the "Last signed in as…" resume card for one-click re-login. This is
    // only an email hint (no silent auth — the refresh token above is cleared,
    // and resuming still requires an OTP), so it doesn't re-authenticate anyone.

    // Clear all user-scoped data (in-memory, localStorage, IndexedDB)
    // to prevent data leakage when switching accounts.
    clearUserData();

    // Drop the persisted active workspace. Without this, the previous user's
    // activeWorkspaceId survives in localStorage ('nula-workspace') and is sent
    // as the X-Workspace-ID header on the next user's requests — they aren't a
    // member of that workspace, so writes (e.g. recording_start → POST
    // /api/entities) 403 until a re-login resyncs. resetForLogout also clears
    // `bootstrapped` so the boot state machine starts clean for the next sign-in.
    useWorkspaceStore.getState().resetForLogout();
    posthog.reset();
    shutdownIntercom();

    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      loginError: null,
      onboardingCompletedAt: undefined,
      nulaUserId: null,
      intercom: null,
    });
  },
}));
